import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import type { Review, ReviewRating } from '../types/review';

interface ReviewParams {
  userId: string;
  limitCount?: number;
  offsetCount?: number;
}

type UserReviews = {
  reviews: ReviewData[];
  hasMore: boolean;
  lastSearchParams: ReviewParams | null;
  loading: boolean;
  error: string | null;
};

export type ReviewData = {
  id: string;
  comment: string;
  created_at: string;
  quote_id: string;
  rating: number;
  reviewed_id: string;
  reviewer_id: string;
  reviewer: {
    first_name: string;
    last_name: string;
    company_name?: string | null;
  };
};

interface ReviewState {
  userReviews: Record<string, UserReviews>;
  loading: boolean;
  error: string | null;
  fetchReviews: (params: ReviewParams) => Promise<void>;
  fetchMoreReviews: (userId: string) => Promise<void>;
  submitReview: (data: Review) => Promise<void>;
  getAverageRating: (userId: string) => Promise<number>;
  doesReviewExists: (quoteId: string, reviewedId: string) => Promise<boolean>;
  calculateOverallRating: (ratings: ReviewRating[]) => number;
}

export const useReviewStore = create<ReviewState>((set, get) => {
  async function fetchAndSetReviews(params: ReviewParams, append = false) {
    const { userId, limitCount = 10, offsetCount = 0 } = params;

    const userReview = get().userReviews[userId];

    if (userReview && userReview.loading) {
      return;
    }

    if (userReview && userReview.reviews.length >= limitCount + offsetCount) {
      // If the user has more reviews than the limit, don't fetch more
      return;
    }

    // Set loading state for this user
    set(state => ({
      userReviews: {
        ...state.userReviews,
        [userId]: {
          ...state.userReviews[userId],
          loading: true,
          error: null,
        },
      },
    }));

    const fetchLimit = limitCount + 1;

    try {
      const { data, error } = await supabase.rpc('fetch_profile_reviews', {
        p_reviewed_id: userId,
        p_limit: fetchLimit,
        p_offset: offsetCount,
      });

      if (error) {
        set(state => ({
          userReviews: {
            ...state.userReviews,
            [userId]: {
              ...state.userReviews[userId],
              loading: false,
              error: error.message,
            },
          },
        }));
        return;
      }

      const reviews: ReviewData[] = ((data ?? []) as ReviewData[]).map(entry => {
        const reviewerObj = entry.reviewer ?? {};
        return {
          id: entry.id,
          quote_id: entry.quote_id,
          reviewer_id: entry.reviewer_id,
          reviewed_id: entry.reviewed_id,
          rating: entry.rating,
          comment: entry.comment,
          created_at: entry.created_at,
          reviewer: {
            first_name: reviewerObj.first_name ?? '',
            last_name: reviewerObj.last_name ?? '',
            company_name: reviewerObj.company_name ?? '',
          },
        };
      });

      set(state => ({
        userReviews: {
          ...state.userReviews,
          [userId]: {
            reviews: append ? [...(state.userReviews[userId]?.reviews ?? []), ...reviews] : reviews,
            hasMore: (data?.length || 0) > limitCount,
            lastSearchParams: { ...params, limitCount, offsetCount },
            loading: false,
            error: null,
          },
        },
      }));
    } catch (error) {
      set(state => ({
        userReviews: {
          ...state.userReviews,
          [userId]: {
            ...state.userReviews[userId],
            loading: false,
            error: (error as Error).message,
          },
        },
      }));
    }
  }

  return {
    userReviews: {},
    loading: false,
    error: null,

    fetchReviews: async (params: ReviewParams) => {
      await fetchAndSetReviews(params);
    },

    fetchMoreReviews: async (userId: string) => {
      const userReview = get().userReviews[userId];
      if (!userReview) return fetchAndSetReviews({ userId });

      const offsetCount = userReview.reviews.length;
      await fetchAndSetReviews({ userId, offsetCount }, true);
    },

    submitReview: async reviewData => {
      set({ loading: true });
      try {
        const { data: user } = await supabase.auth.getUser();
        if (!user.user) throw new Error('Not authenticated');

        const { error } = await supabase.from('reviews').insert([
          {
            quote_id: reviewData.quote_id,
            rating: reviewData.rating,
            comment: reviewData.comment,
            reviewer_id: user.user.id,
            reviewed_id: reviewData.reviewed_id,
          },
        ]);

        if (error) throw error;
        await get().fetchReviews({ userId: reviewData.reviewed_id });
      } catch (error) {
        set({ error: (error as Error).message });
      } finally {
        set({ loading: false });
      }
    },

    getAverageRating: async (userId: string) => {
      try {
        const { data, error } = await supabase.rpc('get_user_average_rating', { user_id: userId });

        if (error) throw error;
        return data;
      } catch (error) {
        console.error('Error getting average rating:', error);
        return 0;
      }
    },

    doesReviewExists: async (quoteId: string, reviewedId: string) => {
      try {
        const { data, error } = await supabase.rpc('review_exists', {
          p_quote_id: quoteId,
          p_reviewed_id: reviewedId,
        });

        if (error) {
          console.error('Error checking review exists:', error);
          return false;
        }

        return data;
      } catch (error) {
        console.error('Error checking review exists:', error);
        return false;
      }
    },

    calculateOverallRating: (ratings: ReviewRating[]): number => {
      if (ratings.length === 0) return 0;
      const sum = ratings.reduce((acc, curr) => acc + curr.rating, 0);
      return Math.round((sum / ratings.length) * 10) / 10;
    },
  };
});
